import { useCallback, useState } from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import {
  fromVersionStatus,
  TEMPLATE_STATUS_LABEL,
  TemplateStatusBadge,
} from "~/pages/Reporting/components/TemplateStatusBadge";
import { formatUserName } from "~/pages/Reporting/utils";
import { SPACING } from "~/styles/variables";
import { DeniedReasonDrawer } from "./DeniedReasonDrawer";
import type {
  GetReportTemplateQuery,
  ReportTemplateVersionStatus as ReportTemplateVersionStatusType,
} from "~/graphql/supergraphTypes";

type GqlUserInfo = NonNullable<
  NonNullable<GetReportTemplateQuery["reportTemplate"]>["updatedByUser"]
>;

type DeniedReviewInfo = {
  deniedReason?: string | null;
  reviewedAt: string;
  reviewedByUser: GqlUserInfo;
};

export type TemplateVersionStatusProps = {
  versionStatus: ReportTemplateVersionStatusType;
  isArchived?: boolean;
  statusByUser?: GqlUserInfo | null;
  statusAt?: string;
  formatDate?: (date: string) => string;
  attributionPrefix?: string;
  showDeniedComment?: boolean;
  deniedReview?: DeniedReviewInfo | null;
};

const buildAttribution = (
  user: GqlUserInfo | null | undefined,
  date: string | undefined,
  formatDate: ((date: string) => string) | undefined,
  prefix = "By",
) => {
  const name = formatUserName(user) ?? "";
  const formattedDate = date && formatDate ? formatDate(date) : "";
  return [name ? `${prefix} ${name}` : "", formattedDate].filter(Boolean).join(" ");
};

/**
 * Backwards-compat: keyed by GraphQL `ReportTemplateVersionStatus` for callers
 * (e.g. AG Grid `valueGetter`s) that resolve labels directly from the enum.
 * New code should prefer `TEMPLATE_STATUS_LABEL` from `TemplateStatusBadge`.
 */
export const STATUS_LABEL: Record<ReportTemplateVersionStatusType, string> = {
  APPROVED: TEMPLATE_STATUS_LABEL.approved,
  DENIED: TEMPLATE_STATUS_LABEL.denied,
  PENDING: TEMPLATE_STATUS_LABEL.pending,
};

const ATTRIBUTION_PREFIX: Record<ReportTemplateVersionStatusType, string> = {
  APPROVED: "Created by",
  DENIED: "By",
  PENDING: "Requested by",
};

export const TemplateVersionStatus = ({
  versionStatus,
  isArchived,
  statusByUser,
  statusAt,
  formatDate,
  attributionPrefix,
  showDeniedComment,
  deniedReview,
}: TemplateVersionStatusProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleViewComment = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const status = fromVersionStatus(versionStatus, isArchived);
  const prefix = attributionPrefix ?? (isArchived ? "By" : ATTRIBUTION_PREFIX[versionStatus]);
  const attribution = buildAttribution(statusByUser, statusAt, formatDate, prefix);
  // Hide the default approved attribution under the status badge, but respect an
  // explicit attributionPrefix from callers (e.g. the templates table "Updated by").
  const hideDefaultApprovedAttribution =
    versionStatus === "APPROVED" && !isArchived && attributionPrefix === undefined;
  const showAttribution = !hideDefaultApprovedAttribution && Boolean(attribution);

  const drawerReview =
    showDeniedComment && deniedReview
      ? {
          reviewerName: formatUserName(deniedReview.reviewedByUser) ?? "",
          reviewedAt: formatDate
            ? formatDate(deniedReview.reviewedAt)
            : deniedReview.reviewedAt,
          deniedReason: deniedReview.deniedReason ?? "",
        }
      : null;

  const showViewReason =
    !isArchived &&
    versionStatus === "DENIED" &&
    showDeniedComment &&
    Boolean(deniedReview?.deniedReason);

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={SPACING.X_SMALL}>
      <TemplateStatusBadge status={status} />
      {showAttribution && <Text variant="secondary-sm">{attribution}</Text>}
      {showViewReason && (
        <>
          <Button displayAsLink onClick={handleViewComment}>
            View reason
          </Button>
          <DeniedReasonDrawer
            isOpen={isDrawerOpen}
            onClose={handleCloseDrawer}
            review={drawerReview}
          />
        </>
      )}
    </Flex>
  );
};
